import {Button, Card, Typography} from 'antd';
import {CardProps} from 'antd/es/card';
import {ChargeFieldsFragment, useChargeQuery} from 'api/generated';
import {PageHeader, PaymentMethodOverview, Stack} from 'components';
import {ChargeStatusTag} from 'components/ChargeStatusTag';
import {ContactDetailsBlock} from 'components/ContactDetailsBlock';
import {CustomerBlock} from 'components/CustomerBlock';
import {PaymentMethodBlock} from 'components/PaymentMethodBlock';
import {PropertyList} from 'components/PropertyList';
import {TraceDetailsBlock} from 'components/TraceDetailsBlock';
import {useAccessLevel} from 'hooks/useAccessLevel';
import {
  canBeCanceled,
  canBeCaptured,
  canBeRefunded,
  canSendPaymentLink,
  canSendPaymentReceipt,
  getChargeDescription
} from 'lib/chargeUtils';
import {getShopLink} from 'lib/formaters';
import {
  acquirerLabels,
  cancellationReasonLabels,
  paymentProcessorLabels,
  refundReasonLabels
} from 'lib/labels';
import moment from 'moment';
import {stringify} from 'querystring';
import React from 'react';
import {Helmet} from 'react-helmet';
import {Link, RouteComponentProps} from 'react-router-dom';
import {CancelPayment} from 'routes/Payment/CancelPayment';
import {RefundPayment} from 'routes/Payment/RefundPayment';
import {SendPaymentReceipt} from 'routes/Payment/SendPaymentReceipt';
import {queryFiltersSchema} from 'routes/Payments';
import {config} from 'stage.config';
import {encodeQueryParams} from 'use-query-params';
import {NotFound} from '../NotFound';
import {CapturePayment} from './CapturePayment';
import {PaymentTimeline} from './PaymentTimeline';
import {SendPaymentLink} from './SendPaymentLink';

const {Text} = Typography;

const getCustomerLink = (customerEmail?: string | null) => {
  if (!customerEmail) return null;
  const queryParams = {
    customerEmail,
    createdAt: [moment().subtract(3, 'months').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')]
  };
  return (
    <Link
      to={{
        pathname: '/payments',
        // @ts-ignore
        search: stringify(encodeQueryParams(queryFiltersSchema, queryParams))
      }}>
      {customerEmail}
    </Link>
  );
};

const getFilteredValueLink = (type: string, value?: string | null) => {
  if (!value) return null;
  const queryParams = {
    [type]: value,
    createdAt: [moment().subtract(3, 'months').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')]
  };
  return (
    <Link
      to={{
        pathname: '/payments',
        // @ts-ignore
        search: stringify(encodeQueryParams(queryFiltersSchema, queryParams))
      }}>
      {value}
    </Link>
  );
};

const getSubscriptionPageLink = (subscriptionId?: string | null, accountId?: string | null) => {
  if (!subscriptionId || !accountId) return null;
  return (
    <Link
      to={{
        pathname: `/accounts/${accountId}/subscriptions/${subscriptionId}`
      }}>
      {subscriptionId}
    </Link>
  );
};

export const Payment: React.FC<RouteComponentProps<{chargeId: string}>> = ({match}) => {
  const {chargeId} = match.params;
  const {isAdmin} = useAccessLevel();
  const {data, loading} = useChargeQuery({
    variables: {id: chargeId},
    context: {useApolloNetworkStatus: false}
  });
  const charge = data?.charge as ChargeFieldsFragment;

  if (!charge && !loading) {
    return (
      <NotFound
        message={
          <>
            Sorry, payment with id <strong>{chargeId}</strong> does not exist
          </>
        }
      />
    );
  }

  const currency = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: charge?.currency || 'EUR'
  });

  const amount = currency.format((charge?.amount ?? 0) / 100);

  const title = charge && (
    <Stack>
      <span>{amount}</span>
      <ChargeStatusTag charge={charge} style={{marginLeft: 15}} />
    </Stack>
  );

  const cardProps: CardProps = {
    style: {marginBottom: 20},
    loading,
    size: 'small'
  };

  return (
    <>
      <Helmet title={getChargeDescription(charge)} />
      <PageHeader loading={loading} title={title}>
        <Stack>
          {canSendPaymentLink(charge) && <SendPaymentLink payment={charge} />}
          {canSendPaymentReceipt(charge) && <SendPaymentReceipt payment={charge} />}
          {canBeRefunded(charge) && <RefundPayment payment={charge} />}
          {canBeCaptured(charge) && <CapturePayment payment={charge} />}
          {canBeCanceled(charge) && <CancelPayment payment={charge} />}
          <Button
            href={`https://${config.hostedPaymentDomain}/payments/${charge?.id}`}
            target="_blank">
            View payment
          </Button>
        </Stack>
      </PageHeader>
      <Card
        title="Payment"
        extra={
          <Text type="secondary" copyable>
            {charge?.id}
          </Text>
        }
        {...cardProps}>
        <PropertyList
          omitEmpty
          properties={[
            {label: 'Store name', value: getShopLink(charge?.shop?.name)},
            {
              label: 'Acquirer',
              value: isAdmin && charge?.acquirer ? acquirerLabels[charge?.acquirer] : null
            },
            {
              label: 'Payment processor',
              value: isAdmin && charge?.processor ? paymentProcessorLabels[charge?.processor] : null
            },
            {
              label: 'Source',
              value: isAdmin ? charge?.traceDetails?.source?.split('/').pop() : null
            },
            {
              label: 'Account ID',
              value: <Link to={`/accounts/${charge?.accountId}`}>{charge?.accountId}</Link>
            },
            ...(charge?.storeId
              ? [
                  {
                    label: 'Store ID',
                    value: (
                      <Link to={`/payments/?storeId=${charge?.storeId}`}>{charge?.storeId}</Link>
                    )
                  }
                ]
              : []),
            {label: 'Date', value: moment.unix(charge?.createdAt).format('lll')},
            {
              label: 'Customer email',
              value: getCustomerLink(charge?.customer?.email)
            },
            {
              label: 'Type',
              value: charge?.pointOfSaleId ? 'QR Payment' : null
            },
            {
              label: 'Payment method',
              value: <PaymentMethodOverview item={charge} listView={false} />
            }
          ]}
        />
      </Card>
      <PaymentTimeline charge={charge} />
      <Card title="Payment details" {...cardProps}>
        <PropertyList
          omitEmpty
          properties={[
            {label: 'Amount', value: amount},
            {
              label: 'Refunded',
              value: charge?.refundedAmount ? currency.format(charge?.refundedAmount / 100) : null
            },
            {
              label: 'Refund reason',
              value: charge?.lastRefundReason ? refundReasonLabels[charge?.lastRefundReason] : null
            },
            {
              label: 'Cancellation reason',
              value: charge?.cancellationReason
                ? cancellationReasonLabels[charge?.cancellationReason]
                : null
            },
            {label: 'Description', value: charge?.description},
            {label: 'Descriptor', value: charge?.descriptor},
            {label: 'Status', value: charge?.status},
            {label: 'Status code', value: charge?.statusCode},
            {label: 'Status message', value: charge?.statusMessage},
            ...(isAdmin
              ? [
                  {label: 'Provider status code', value: charge?.providerStatusCode},
                  {label: 'Provider status message', value: charge?.providerStatusMessage},
                  {label: 'Issuer status code', value: charge?.issuerStatusCode},
                  {label: 'Issuer status message', value: charge?.issuerStatusMessage},
                  {
                    label: 'Provider reference ID',
                    value: getFilteredValueLink('providerReferenceId', charge?.providerReferenceId),
                    copyable: charge?.providerReferenceId ? {text: charge?.providerReferenceId} : {}
                  }
                ]
              : []),
            {
              label: 'Order ID',
              value: getFilteredValueLink('orderId', charge?.orderId),
              copyable: charge?.orderId ? {text: charge?.orderId} : {}
            },
            {
              label: 'Subscription ID',
              value: getSubscriptionPageLink(charge?.subscriptionId, charge?.accountId),
              copyable: charge?.subscriptionId ? {text: charge?.subscriptionId} : {}
            },
            {
              label: 'Sequence ID',
              value: getFilteredValueLink('sequenceId', charge?.sequenceId),
              copyable: charge?.sequenceId ? {text: charge?.sequenceId} : {}
            },
            {label: 'Authorization code', value: charge?.authorizationCode}
          ]}
        />
      </Card>
      {charge?.customer && (
        <Card title="Customer" {...cardProps}>
          <CustomerBlock
            customer={charge.customer}
            countryCode={charge.traceDetails?.countryCode || null}
          />
        </Card>
      )}
      {charge?.paymentMethod && (
        <Card title="Payment method" {...cardProps}>
          <PaymentMethodBlock
            paymentMethod={charge.paymentMethod}
            countryCode={charge.paymentMethod.card?.country || charge.paymentMethod.cardPresent?.country}
          />
        </Card>
      )}
      {charge?.billingDetails && (
        <Card title="Billing details" {...cardProps}>
          <ContactDetailsBlock contactDetails={charge.billingDetails} />
        </Card>
      )}
      {charge?.shippingDetails && (
        <Card title="Shipping details" {...cardProps}>
          <ContactDetailsBlock contactDetails={charge.shippingDetails} />
        </Card>
      )}
      {charge?.sessionDetails && (
        <Card title="Session" {...cardProps}>
          <TraceDetailsBlock traceDetails={charge.sessionDetails} />
        </Card>
      )}
    </>
  );
};
